import Actions from 'actions'
import OralScoreTheme from 'components/common/icons/timelineV2/themes/OralScoreTheme'
import ScanTrackingTheme from 'components/common/icons/timelineV2/themes/ScanTrackingTheme'
import TxTrackerTheme from 'components/common/icons/timelineV2/themes/TxTrackerTheme'
import TimeBasedTheme from 'components/common/icons/timelineV2/themes/TimeBasedTheme'
import { DEFAULT_MINI_TIMELINE_THEME, MiniTimelineThemes } from 'consts/timelineConsts'
import hasSupportTimeBasedAccess from './supportTimeBasedAccess'
import useFeatureFlags from 'hooks/useFeatureFlags'
import usePlanLimits from 'hooks/usePlanLimits'
import useRolePermissions from 'hooks/useRolePermissions'
import { isEmpty } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

const THEME_LOCAL_STORAGE_KEY = 'miniTimeline.theme'

export default () => {
  const dispatch = useDispatch()
  const { isHiUser } = useRolePermissions()
  const { theme } = useSelector(state => state.timelineReducer.miniTimeline)
  const doctorId = useSelector(state => state.profileReducer.doctor?.id)
  const ownFeatureFlags = useSelector(state => state.profileReducer.doctor?.user?.featureFlags?.flags)
  const mpPracticeFFs = useSelector(state => state.multiPracticeReducer.practiceFFs)

  // Read off the signed-in person's own record rather than through useFeatureFlags, which mixes in
  // the viewed practice's flags. Not because a practice could switch this off - the grant UI
  // deletes a key rather than storing false, so that spread can only ever turn a flag on - but
  // because practiceFFs is fetched only for HI, DSO and Admin users, and is never cleared when the
  // practice in view changes. A DevAdmin or OpsAdmin would be reading a blob that is either empty
  // or left over from the last practice. Their own record is neither.
  const hasSupportAccess = useMemo(() => hasSupportTimeBasedAccess(ownFeatureFlags), [ownFeatureFlags])

  const [hasLoadedTheme, setHasLoadedTheme] = useState(false)

  const {
    oralHygieneScore: oralHygieneScoreFF,
    tagScanForNonHI: tagScanForNonHIFF,
    treatmentTracker: treatmentTrackerFF,
    txTrackerMiniTimeline: txTrackerMiniTimelineFF,
    timeBasedMiniTimeline: timeBasedMiniTimelineFF
  } = useFeatureFlags()
  const { withScanTracking } = usePlanLimits()

  const themeOptions = useMemo(
    () =>
      [
        {
          type: MiniTimelineThemes.ScanTracking,
          icon: <ScanTrackingTheme />,
          condition: withScanTracking || isHiUser || tagScanForNonHIFF
        },
        {
          type: MiniTimelineThemes.OralScore,
          icon: <OralScoreTheme />,
          condition: oralHygieneScoreFF
        },
        {
          type: MiniTimelineThemes.TxTracker,
          icon: <TxTrackerTheme />,
          condition: treatmentTrackerFF && txTrackerMiniTimelineFF
        },
        {
          type: MiniTimelineThemes.TimeBased,
          icon: <TimeBasedTheme />,
          condition: timeBasedMiniTimelineFF || hasSupportAccess
        }
      ].filter(theme => theme.condition),
    [
      withScanTracking,
      isHiUser,
      tagScanForNonHIFF,
      oralHygieneScoreFF,
      treatmentTrackerFF,
      txTrackerMiniTimelineFF,
      timeBasedMiniTimelineFF,
      hasSupportAccess
    ]
  )

  const selectTheme = useCallback(
    theme => {
      localStorage.setItem(THEME_LOCAL_STORAGE_KEY, theme)
      dispatch(Actions.setMiniTimelineTheme({ theme }))
    },
    [dispatch]
  )

  useEffect(() => {
    // Waits for the profile, not only for the HI practice flags. The support flag is read off the
    // signed-in person, so before that arrives the time-based option is absent - and this effect
    // does not merely pick a theme, it writes the choice to localStorage and then latches. Running
    // early would overwrite a saved 'time-based' preference with the fallback, permanently.
    // Gated on the doctor rather than on the flags: someone with no FeatureFlags record at all
    // would otherwise wait forever.
    if (hasLoadedTheme || !doctorId || (isHiUser && isEmpty(mpPracticeFFs))) {
      return
    }

    const userThemePreference = localStorage.getItem(THEME_LOCAL_STORAGE_KEY)
    const preferredTheme = themeOptions.find(theme => theme.type === userThemePreference)

    if (preferredTheme) {
      selectTheme(preferredTheme.type)
    } else {
      const fallbackTheme = themeOptions[0]?.type || DEFAULT_MINI_TIMELINE_THEME
      selectTheme(fallbackTheme)
    }

    setHasLoadedTheme(true)
  }, [themeOptions, selectTheme, hasLoadedTheme, doctorId, isHiUser, mpPracticeFFs])

  return {
    themeOptions,
    selectTheme,
    selectedTheme: theme
  }
}
